import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { Box, Typography, Divider, Chip, Stack, CircularProgress } from "@mui/material";
import { VisibilityOutlined } from "@mui/icons-material";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from "recharts";

import { CardLayout, EmptyState, IconButton } from "../v3";
import { TaskService } from "../../services";
import { useHasPermission } from "../../utils";
import { VIEW_ALL_TASK, VIEW_ASSIGNED_TASK } from "../../constants";

export const TaskOverview = () => {
  const [tasks, setTasks] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);

  const { id } = useParams();
  const navigate = useNavigate();

  const hasViewAllTask = useHasPermission(VIEW_ALL_TASK);
  const hasViewAssignedTask = useHasPermission(VIEW_ASSIGNED_TASK);

  useEffect(() => {
    fetchData(id);
  }, [id]);

  const fetchData = async (projectId) => {
    setLoading(true);
    try {
      const { data: tasks } = await TaskService.getTaskList({ projectId });
      setTasks(tasks?.rows);
      setTotalCount(tasks?.count);
    } catch (error) {
      toast.error("Failed to fetch tasks");
      console.error("🚀 ~ fetchData ~ error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleViewTask = () => {
    navigate(`tasks`);
  };

  const chartData = React.useMemo(() => {
    if (!tasks?.length) return [];
    const statusCounts = tasks.reduce((acc, task) => {
      const status = task.status || "UNKNOWN";
      acc[status] = (acc[status] || 0) + 1;
      return acc;
    }, {});

    return [
      { name: "Completed", value: statusCounts.DONE || 0, color: "#674BF5" },
      { name: "Ongoing", value: statusCounts.INPROGRESS || 0, color: "#B39DDB" },
      { name: "To Do", value: statusCounts.TODO || 0, color: "#311B92" },
    ];
  }, [tasks]);

  const maxValue = Math.max(...chartData.map((d) => d.value), 0);

  return (
    <CardLayout sx={{ margin: "11px" }}>
      {/* header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 1.5,
        }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 600, color: "#674BF5" }}>
          Task Overview
        </Typography>
        {(hasViewAllTask || hasViewAssignedTask) && (
          <IconButton
            icon={<VisibilityOutlined />}
            buttonText="All Task"
            tooltip="View Tasks"
            onClick={handleViewTask}
            styles={{ color: "#757575", backgroundColor: "#F4F4F4" }}
            hoverStyles={{ backgroundColor: "#EBE9F9" }}
          />
        )}
      </Box>

      {/* body */}
      {loading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            py: 4,
          }}
        >
          <CircularProgress size={32} sx={{ color: "#674BF5" }} />
        </Box>
      ) : tasks.length > 0 ? (
        <>
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-end",
              justifyContent: "space-between",
              height: 150,
              marginY: 2,
            }}
          >
            {/* bar Chart */}
            <Box sx={{ flex: 1.5, height: "100%" }}>
              <ResponsiveContainer width="65%" height="100%">
                <BarChart data={chartData} barGap={50} margin={{ top: 15, right: 0, left: 0, bottom: 10 }}>
                  <XAxis dataKey="name" hide />
                  <YAxis
                    domain={[0, maxValue + 2]}
                    tickCount={4}
                    tickMargin={30}
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: "#000", fontSize: 12, dy: -2, fontWeight: 600 }}
                  />
                  <Tooltip
                    cursor={{ fill: "transparent" }}
                    contentStyle={{
                      backgroundColor: "#fff",
                      border: "1px solid #E0E0E0",
                      fontSize: "0.75rem",
                      borderRadius: 10,
                    }}
                  />
                  <Bar dataKey="value" radius={[30, 30, 30, 30]} barSize={40}>
                    {chartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Box>

            {/* status list */}
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "flex-end",
                height: "100%",
                pr: 2,
              }}
            >
              {chartData.map((item, index) => (
                <Stack key={index} direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
                  <Box
                    sx={{
                      width: 15,
                      height: 15,
                      borderRadius: "50%",
                      backgroundColor: item.color,
                    }}
                  />
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 600, letterSpacing: "1px" }}
                  >{`${item.name} (${item.value})`}</Typography>
                </Stack>
              ))}
            </Box>
          </Box>
          <Divider sx={{ my: 2, borderColor: "#797979" }} />
          {/* footer */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              gap: 1,
            }}
          >
            <Typography variant="h4" sx={{ fontWeight: 700, color: "#674BF5", lineHeight: 1 }}>
              {totalCount}
            </Typography>
            <Chip
              label="Total tasks"
              sx={{
                backgroundColor: "#E8EDFB",
                color: "#674BF5",
                fontWeight: 500,
                borderRadius: "20px",
                fontSize: "0.9rem",
              }}
            />
          </Box>
        </>
      ) : (
        <EmptyState title="No tasks found" subtitle="Try creating a task to get started!" />
      )}
    </CardLayout>
  );
};
